// Schema for slide content that we'll send to/receive from OpenAI

import type { FinsterQueryMode } from "./api/finster/constants";
import type { AgentTimelineState } from "./services/ai/agentStream";
import type { SkillTurnMetadata } from "../shared/skills/turnMetadata";
import type { FactsetChartParameters } from "../shared/factset/chartCreatorTypes";

export interface TableCellData {
  rowCount: number;
  colCount: number;
  rows: string[][]; // 2D array of current cell contents
  columnWidths?: number[]; // Points, ordered left-to-right
  rowHeights?: number[]; // Points, ordered top-to-bottom
  mergedAreas?: TableMergedArea[];
  styleSnapshot?: TableStyleSnapshot;
  semanticHints?: TableSemanticHints;
}

// Geometric shape types supported by PowerPoint
export type GeometricShapeType =
  | "rectangle"
  | "ellipse"
  | "triangle"
  | "diamond"
  | "pentagon"
  | "hexagon"
  | "star4"
  | "star5"
  | "star6"
  | "roundedRectangle"
  | "parallelogram"
  | "trapezoid"
  | "arrow"
  | "arrowLeft"
  | "arrowUp"
  | "arrowDown"
  | "callout"
  | "cloud"
  | "heart"
  | "lightning"
  | "bracePair"
  | "bracketPair"
  | "plus"
  | "minus"
  | "line"
  | "straightConnector";

export type ParagraphHorizontalAlignment =
  | "Left"
  | "Center"
  | "Right"
  | "Justify"
  | "JustifyLow"
  | "Distributed"
  | "ThaiDistributed";

export type ParagraphBulletType = "None" | "Numbered" | "Unnumbered";

export interface ParagraphBulletSet {
  visible?: boolean;
  type?: ParagraphBulletType;
  style?: string;
}

export interface ParagraphFontSet {
  size?: number;
  family?: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
}

export interface ParagraphRun {
  start: number;
  length: number;
  font?: ParagraphFontSet;
}

export interface ParagraphSet {
  horizontalAlignment?: ParagraphHorizontalAlignment;
  indentLevel?: number;
  bullet?: ParagraphBulletSet;
  font?: ParagraphFontSet;
  text?: string; // replace only the targeted paragraph(s)
  runs?: ParagraphRun[]; // substring formatting (0-based char offsets)
}

export type ParagraphTarget = { type: "all" } | { type: "indices"; indices: number[] };

export interface ParagraphOp {
  target: ParagraphTarget;
  set: ParagraphSet;
}

export interface ParagraphContent {
  index: number; // 0-indexed paragraph
  text: string;
  fontSize?: number;
  fontFamily?: string;
  textColor?: string;
  bold?: boolean;
  italic?: boolean;
  bulletVisible?: boolean;
  bulletType?: string; // e.g. "Alphanumeric", "Picture", "Character"
  bulletStyle?: string; // e.g. "ArabicPeriod", "RomanLCPeriod"
}

export interface ShapeContent {
  id: string;
  name: string;
  type: "textBox" | "image" | "geometricShape" | "chart" | "oleObject" | "table" | "line" | "group" | "unknown";
  geometricType?: GeometricShapeType; // Present when type is "geometricShape"
  // Semantic placeholder role surfaced by the host (e.g. "title", "body",
  // "centerTitle", "subTitle"). Optional — not all hosts expose this.
  placeholderType?: string;
  text?: string;
  paragraphs?: ParagraphContent[]; // 0-indexed paragraph text snapshots for model targeting
  tableData?: TableCellData; // Present when type is "table"
  // Present and true only when the host confirmed this shape is PresLink-linked.
  // Absence means "not confirmed" (probe skipped, failed, or found no link) and
  // must never be read as proof the shape is unlinked.
  preslinkLinked?: true;
  fillColor?: string; // Shape fill color
  lineColor?: string; // Shape outline color
  lineWidth?: number; // Shape outline width in points
  // Text styling (extracted from first text run)
  fontSize?: number; // Font size in points
  fontFamily?: string; // Font name (e.g. "Arial", "Calibri")
  textColor?: string; // Text color as hex (e.g. "#000000")
  bold?: boolean;
  italic?: boolean;
  horizontalAlignment?: string; // e.g. "Center", "Left", "Right", "Justify"
  verticalAlignment?: string; // e.g. "Middle", "Top", "Bottom"
  left: number;
  top: number;
  width: number;
  height: number;
  // Nested GroupItems. Present when type is "group" and children were extracted.
  shapes?: ShapeContent[];
  // Set on children of a group; omitted on top-level slide shapes.
  parentGroupId?: string;
}

export interface SlideContent {
  id: string;
  index: number; // Internal 0-based deck index used for host operations
  slideNumber?: number; // Displayed PowerPoint slide number; may start at 0/custom offset
  shapes: ShapeContent[];
  // Layout name reported by the host (e.g. "Title", "Title and Content",
  // "Two Content"). Optional — present when the host exposes it.
  layout?: string;
  customLayoutName?: string;
  slideMasterName?: string;
  displayMasterShapes?: boolean;
  followMasterBackground?: boolean;
}

export interface ReferenceSlideImage {
  slideId: string;
  image: string;
}

export interface DeckLayoutPlaceholder {
  id?: string;
  name?: string;
  type?: string;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  placeholderFormat?: {
    type?: string;
    name?: string;
    containedType?: string;
  };
}

export interface DeckLayoutUsedBySlide {
  id: string;
  index: number;
  slideNumber?: number;
}

export interface DeckLayoutCatalogEntry {
  layoutName?: string;
  matchingName?: string;
  slideMasterName?: string;
  layoutType?: string;
  dimensions?: { width?: number; height?: number };
  inheritsMasterShapes?: boolean;
  followsMasterBackground?: boolean;
  placeholders?: DeckLayoutPlaceholder[];
  usedBySlides?: DeckLayoutUsedBySlide[];
}

export interface DeckLayoutCatalog {
  layouts: DeckLayoutCatalogEntry[];
}

// Stable slide reference used by tab/session state
export interface SlideRef {
  id: string;
  index: number;
  slideNumber?: number; // Displayed PowerPoint slide number; may start at 0/custom offset
  /**
   * M1 fix: optional `openPresentationId` of the file this slide ref was
   * queried against. The slide-selection poll in `useTabs` runs every
   * 500ms; if the user switches files mid-tick, the response can come back
   * after the React state has already swapped to the new file. The poll
   * drops the result when `openPresentationId` doesn't match the current
   * documentId-derived presentation id, preventing a "ghost tab" from the
   * outgoing file's slide id landing in the incoming file's tab list.
   *
   * Producers SHOULD populate this when the host returns it (FactSet
   * `GetActivePresentationContext` does). Consumers MUST treat it as
   * optional — Office.js / non-FactSet hosts may not include it.
   */
  openPresentationId?: string;
}

export interface PresentationContent {
  slides: SlideContent[];
  selectedSlideIndex: number;
  slideWidth?: number; // Actual slide width in points (default 960 for 16:9)
  slideHeight?: number; // Actual slide height in points (default 540 for 16:9)
  deckLayoutCatalog?: DeckLayoutCatalog;
  /**
   * Optional live theme palette from the open deck (dark/light/accent1–6,
   * hyperlink, fonts). Chat extract attaches this when available so chart
   * routes can copy accent colors; missing/invalid theme data is omitted
   * and does not fail the turn.
   */
  themeColors?: ThemeColors;
  /**
   * True deck slide count when `slides` is a scoped subset of the
   * presentation (e.g. the windowed chat extract returns only 5 of 200
   * slides). Producers that return the full deck SHOULD leave this
   * undefined; consumers that need the true deck size MUST fall back to
   * `slides.length` when this is undefined.
   *
   * Currently set by `extractWindowedPresentationContent` in
   * `services/slideService.ts`; consumed by `buildRequestContextEnvelope`
   * in `utils/ai.ts` for `deckContext.totalSlides` so the LLM sees the
   * real deck size, not the window size.
   */
  totalDeckSlides?: number;
}

export interface ThemeColors {
  dark1: string;
  dark2: string;
  light1: string;
  light2: string;
  accent1: string;
  accent2: string;
  accent3: string;
  accent4: string;
  accent5: string;
  accent6: string;
  hyperlink: string;
  followedHyperlink: string;
  majorFont?: string;
  minorFont?: string;
}

export interface TitlePlaceholderHint {
  left: number;
  top: number;
  width: number;
  height: number;
  fontSize?: number;
  fontFamily?: string;
  textColor?: string;
  bold?: boolean;
}

export interface SlideLayoutInfo {
  blankLayoutId?: string;
  titlePlaceholder?: TitlePlaceholderHint;
}

// Table border style (matches PowerPoint API)
export interface TableBorderStyle {
  color?: string; // Border color (hex or named like "black", "red")
  weight?: number; // Border weight in points
  /** Raw V1 LineFormat transparency: 0 = opaque, 1 = fully transparent. */
  transparency?: number;
  /** Raw V1 LineFormat visibility; true does not override full transparency. */
  visible?: boolean;
  /** Derived rendered state after visibility, transparency, and weight are reconciled. */
  effectiveVisible?: boolean;
}

// Table cell borders
export interface TableCellBorders {
  top?: TableBorderStyle;
  bottom?: TableBorderStyle;
  left?: TableBorderStyle;
  right?: TableBorderStyle;
}

// Table cell properties (matches PowerPoint TableCellProperties)
export interface TableCellProperties {
  fill?: {
    color?: string; // Fill color (hex or named)
    transparency?: number; // 0-1 transparency
  };
  font?: {
    color?: string; // Font color
    name?: string; // Font name (e.g. "Arial")
    size?: number; // Font size in points
    bold?: boolean;
    italic?: boolean;
    allCaps?: boolean;
  };
  borders?: TableCellBorders;
  horizontalAlignment?: "left" | "center" | "right" | "justify" | "distributed";
  verticalAlignment?: "top" | "middle" | "bottom";
  indentLevel?: number;
  margins?: {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
  };
  textRuns?: Array<{
    text: string;
    font?: {
      color?: string;
      name?: string;
      size?: number;
      bold?: boolean;
      italic?: boolean;
      allCaps?: boolean;
    };
  }>;
}

// Merged area definition
export interface TableMergedArea {
  rowIndex: number; // Starting row (0-indexed)
  columnIndex: number; // Starting column (0-indexed)
  rowCount: number; // Number of rows to merge
  columnCount: number; // Number of columns to merge
}

export interface TableStyleSettingsData {
  style?: string;
  areRowsBanded?: boolean;
  areColumnsBanded?: boolean;
  isFirstRowHighlighted?: boolean;
  isFirstColumnHighlighted?: boolean;
  isLastRowHighlighted?: boolean;
  isLastColumnHighlighted?: boolean;
}

export interface TableStyleSnapshot {
  tableStyle?: string;
  styleSettings?: TableStyleSettingsData;
  uniformCellProperties?: TableCellProperties;
  cellOverrides?: Array<{
    row: number;
    col: number;
    set: TableCellProperties;
  }>;
  /** Diagnostic only; must equal cellOverrides.length when emitted. */
  cellOverrideCount?: number;
}

export interface TableSemanticHints {
  headerRowIndices?: number[];
  numericColumnIndices?: number[];
}

export type TableStyleTarget =
  | { type: "all" }
  | { type: "row"; index: number }
  | { type: "column"; index: number }
  | { type: "cell"; row: number; col: number }
  | {
      type: "range";
      startRow: number;
      startCol: number;
      rowCount: number;
      colCount: number;
    };

export interface TableStyleUpdate {
  target: TableStyleTarget;
  set: TableCellProperties;
}

export type TableStructureOp =
  | { op: "addRow"; index: number; count?: number }
  | { op: "deleteRow"; index: number; count?: number }
  | { op: "addColumn"; index: number; count?: number }
  | { op: "deleteColumn"; index: number; count?: number }
  | {
      op: "mergeCells";
      rowIndex: number;
      columnIndex: number;
      rowCount: number;
      columnCount: number;
    }
  | {
      op: "splitCell";
      rowIndex: number;
      columnIndex: number;
      rowCount: number;
      columnCount: number;
    }
  | {
      op: "resizeCellSpan";
      rowIndex: number;
      columnIndex: number;
      rowCount: number;
      columnCount: number;
    };

export interface TableClearOp {
  mode: "all" | "text_only" | "format_only";
}

export interface TableFitPolicy {
  mode?: "none" | "fit_with_font_shrink";
  minFontSizePt?: number;
  maxColumnScaleDownPct?: number;
}

export interface TableLayoutRegion {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface TableAvoidRegion {
  kind?: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface TableLayoutSpec {
  targetRegion?: TableLayoutRegion;
  avoidRegions?: TableAvoidRegion[];
  fitPolicy?: TableFitPolicy;
  reflowSiblings?: "none" | "shift_down" | "shift_right";
}

export interface TablePolicies {
  preserveUnspecifiedFormatting?: boolean;
  onUnsupported?: "diagnostic" | "best_effort";
}

// Unified table operation contract for create/edit/replace/relayout.
export interface TableData {
  action?: "create" | "edit" | "replace" | "relayout";
  targetShapeId?: string;

  // Table matrix payload used by create and replace operations.
  rows?: string[][];
  uniformCellProperties?: TableCellProperties;
  specificCellProperties?: TableCellProperties[][];
  columnWidths?: number[];
  rowHeights?: number[];
  mergedAreas?: TableMergedArea[];
  tableStyle?: string;
  styleSettings?: TableStyleSettingsData;

  // In-place edit payload.
  textUpdates?: CellUpdate[];
  styleUpdates?: TableStyleUpdate[];
  structureOps?: TableStructureOp[];
  clearOps?: TableClearOp[];
  styleSettingsUpdate?: TableStyleSettingsData;

  // Layout payload for edit/replace/relayout.
  layoutSpec?: TableLayoutSpec;
  policies?: TablePolicies;

  // Legacy compatibility hint.
  hasHeaderRow?: boolean;
}

// In-slide chart contract aligned with FactSet COM chart wire format
// (see f2_docs/charting/chart-schema-preview.md).
// All `categories`, `values`, `xValues`, `bubbleSizes` are stringified JSON
// arrays (e.g. "[1,2,3]") to match the goldens in f2_docs/charting/operations.*.json.
// `colorRgb` is a 6-digit uppercase hex string WITHOUT a leading '#'
// (e.g. "2B5680").

export type XlChartType =
  | "xlColumnClustered"
  | "xlColumnStacked"
  | "xlColumnStacked100"
  | "xlBarClustered"
  | "xlBarStacked"
  | "xlBarStacked100"
  | "xlLine"
  | "xlLineMarkers"
  | "xlLineStacked"
  | "xlLineStackedMarkers"
  | "xlPie"
  | "xlPieExploded"
  | "xlDoughnut"
  | "xlDoughnutExploded"
  | "xlArea"
  | "xlAreaStacked"
  | "xlAreaStacked100"
  | "xlXYScatter"
  | "xlXYScatterLines"
  | "xlXYScatterLinesNoMarkers"
  | "xlBubble"
  | "xlBubble3DEffect"
  | "xlRadar"
  | "xlRadarFilled";

export type XlAxisType = "xlCategory" | "xlValue" | "xlSeriesAxis";

export type XlAxisGroup = "xlPrimary" | "xlSecondary";

export type XlTickMark =
  | "xlTickMarkNone"
  | "xlTickMarkInside"
  | "xlTickMarkOutside"
  | "xlTickMarkCross";

export type XlLegendPosition =
  | "xlLegendPositionTop"
  | "xlLegendPositionBottom"
  | "xlLegendPositionLeft"
  | "xlLegendPositionRight"
  | "xlLegendPositionCorner";

export type XlMarkerStyle =
  | "xlMarkerStyleAutomatic"
  | "xlMarkerStyleNone"
  | "xlMarkerStyleSquare"
  | "xlMarkerStyleCircle"
  | "xlMarkerStyleDiamond"
  | "xlMarkerStyleTriangle"
  | "xlMarkerStyleDash"
  | "xlMarkerStyleDot"
  | "xlMarkerStylePlus"
  | "xlMarkerStyleStar"
  | "xlMarkerStyleX";

export type XlDataLabelPosition =
  | "xlLabelPositionAbove"
  | "xlLabelPositionBelow"
  | "xlLabelPositionCenter"
  | "xlLabelPositionLeft"
  | "xlLabelPositionRight"
  | "xlLabelPositionOutsideEnd"
  | "xlLabelPositionInsideEnd"
  | "xlLabelPositionInsideBase"
  | "xlLabelPositionBestFit";

export interface ChartFontInfo {
  name?: string;
  size?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  colorRgb?: string;
}

export interface ChartFillInfo {
  isVisible?: boolean;
  colorRgb?: string;
}

export interface ChartLineInfo {
  isVisible?: boolean;
  colorRgb?: string;
  weight?: number;
  dashStyle?: string;
}

export interface ChartAreaInfo {
  fill?: ChartFillInfo;
  line?: ChartLineInfo;
  font?: ChartFontInfo;
}

export interface PlotAreaInfo {
  fill?: ChartFillInfo;
  line?: ChartLineInfo;
}

export interface ChartMarkerInfo {
  style?: XlMarkerStyle;
  size?: number;
  fill?: ChartFillInfo;
  line?: ChartLineInfo;
}

export interface ChartDataLabelsInfo {
  showValue?: boolean;
  showCategoryName?: boolean;
  showSeriesName?: boolean;
  showPercentage?: boolean;
  showBubbleSize?: boolean;
  showLegendKey?: boolean;
  position?: XlDataLabelPosition;
  numberFormat?: string;
  separator?: string;
  font?: ChartFontInfo;
  fill?: ChartFillInfo;
  border?: ChartLineInfo;
}

export interface ChartPointInfo {
  index: number;
  fill?: ChartFillInfo;
  line?: ChartLineInfo;
  explosion?: number;
  hasDataLabel?: boolean;
  dataLabel?: ChartDataLabelsInfo;
}

export interface ChartTitleInfo {
  hasTitle?: boolean;
  text?: string;
  includeInLayout?: boolean;
  font?: ChartFontInfo;
}

export interface ChartLegendInfo {
  hasLegend?: boolean;
  position?: XlLegendPosition;
  includeInLayout?: boolean;
  font?: ChartFontInfo;
}

export interface ChartAxisInfo {
  axisType: XlAxisType;
  axisGroup?: XlAxisGroup;
  enabled?: boolean;
  hasTitle?: boolean;
  titleText?: string;
  titleFont?: ChartFontInfo;
  minimumScale?: number;
  maximumScale?: number;
  majorUnit?: number;
  minorUnit?: number;
  hasMajorGridlines?: boolean;
  hasMinorGridlines?: boolean;
  reversePlotOrder?: boolean;
  numberFormat?: string;
  tickLabelFont?: ChartFontInfo;
  majorTickMark?: XlTickMark;
  minorTickMark?: XlTickMark;
  axisLine?: ChartLineInfo;
}

export interface ChartSeriesInfo {
  index?: number;
  name: string;
  /** Stringified JSON array of category labels (e.g. "[\"Q1\",\"Q2\"]"). Optional for scatter/bubble. */
  categories?: string;
  /** Stringified JSON array of numeric values (e.g. "[120.4,135.2]"). */
  values: string;
  /** Stringified JSON array of x-values (scatter/bubble only). */
  xValues?: string;
  /** Stringified JSON array of bubble sizes (bubble only). */
  bubbleSizes?: string;
  /** Per-series chart type override for combo charts. */
  chartType?: XlChartType;
  axisGroup?: XlAxisGroup;
  plotOrder?: number;
  smooth?: boolean;
  explosion?: number;
  invertIfNegative?: boolean;
  hasDataLabels?: boolean;
  fill?: ChartFillInfo;
  line?: ChartLineInfo;
  marker?: ChartMarkerInfo;
  dataLabels?: ChartDataLabelsInfo;
  points?: ChartPointInfo[];
}

export interface ChartSeriesEditInfo {
  /** 0-based series index in the existing chart. */
  index: number;
  name?: string;
  /** Stringified JSON array of category labels (e.g. "[\"Q1\",\"Q2\"]"). */
  categories?: string;
  /** Stringified JSON array of numeric values (e.g. "[120.4,135.2]"). */
  values?: string;
  /** Stringified JSON array of x-values (scatter/bubble only). */
  xValues?: string;
  /** Stringified JSON array of bubble sizes (bubble only). */
  bubbleSizes?: string;
  /** Per-series chart type override for existing-chart combo edits. */
  chartType?: XlChartType;
  axisGroup?: XlAxisGroup;
  plotOrder?: number;
  smooth?: boolean;
  explosion?: number;
  invertIfNegative?: boolean;
  hasDataLabels?: boolean;
  fill?: ChartFillInfo;
  line?: ChartLineInfo;
  marker?: ChartMarkerInfo;
  dataLabels?: ChartDataLabelsInfo;
  points?: ChartPointInfo[];
}

export interface ChartAxisEditInfo {
  axisType: XlAxisType;
  axisGroup?: XlAxisGroup;
  enabled?: boolean;
  hasTitle?: boolean;
  titleText?: string;
  titleFont?: ChartFontInfo;
  minimumScale?: number;
  maximumScale?: number;
  majorUnit?: number;
  minorUnit?: number;
  hasMajorGridlines?: boolean;
  hasMinorGridlines?: boolean;
  reversePlotOrder?: boolean;
  numberFormat?: string;
  tickLabelFont?: ChartFontInfo;
  majorTickMark?: XlTickMark;
  minorTickMark?: XlTickMark;
  axisLine?: ChartLineInfo;
  majorGridLine?: ChartLineInfo;
  minorGridLine?: ChartLineInfo;
}

export interface ChartPositionInfo {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ChartGroupInfo {
  /** 0-based chart group index. A simple chart usually has one group: index 0. */
  index: number;
  /** Bar/column gap width (%). Larger values make bars thinner. */
  gapWidth?: number;
  /** Bar/column overlap (%). */
  overlap?: number;
  varyByCategories?: boolean;
  /** Pie rotation (degrees). */
  firstSliceAngle?: number;
  /** Doughnut hole size (%). */
  doughnutHoleSize?: number;
  splitType?: string;
  splitValue?: number;
  secondPlotSize?: number;
  bubbleScale?: number;
  showNegativeBubbles?: boolean;
  sizeRepresents?: string;
  hasSeriesLines?: boolean;
  hasDropLines?: boolean;
  hasHiLoLines?: boolean;
  hasUpDownBars?: boolean;
  axisGroup?: XlAxisGroup;
}

/**
 * In-slide chart contract emitted by the LLM under `shape.action="create"` with a `chart` body.
 * Mirrors the FactSet COM `Chart` operation wire format documented in
 * f2_docs/charting/chart-schema-preview.md. The same payload is forwarded to
 * PerformBatchPptOperations on the FactSet COM bridge, or expanded into a
 * two-pass PptxGenJS slide insertion via chartTwoPassTranslator in Office.js mode.
 */
export interface ChartInfo {
  /** Discriminator. Always "Chart". */
  type?: "Chart";
  id?: number;
  index?: number;
  name?: string;
  /** Required for creates; optional for sparse existing-chart modify patches. */
  chartType?: XlChartType;
  title?: ChartTitleInfo;
  legend?: ChartLegendInfo;
  /** Required for creates; optional for sparse existing-chart modify patches. */
  series?: ChartSeriesInfo[];
  axes?: ChartAxisInfo[];
  /** Position in points (matches f2_docs golden wire format). */
  position?: ChartPositionInfo;
  /** Per-chart-group settings such as bar/column gap width and overlap. */
  chartGroups?: ChartGroupInfo[];
  /** Doughnut hole size (%) for doughnut charts. */
  doughnutHoleSize?: number;
  /** Pie rotation (degrees) for pie charts. */
  firstSliceAngle?: number;
  /** Bar/column gap width (%). */
  gapWidth?: number;
  /** Bar/column overlap (%). */
  overlap?: number;
}

/**
 * Existing-chart patch contract for host-verified in-place edits.
 * This intentionally excludes chartType, gap/overlap, remove-series, and
 * replacement/rebuild workflows.
 */
export interface ChartEditInfo {
  title?: ChartTitleInfo;
  legend?: ChartLegendInfo;
  series?: ChartSeriesEditInfo[];
  axes?: ChartAxisEditInfo[];
  chartArea?: ChartAreaInfo;
  plotArea?: PlotAreaInfo;
}

// Cell update for modifying existing tables
export interface CellUpdate {
  row: number; // 0-indexed row
  col: number; // 0-indexed column
  text: string; // New cell text
}

// Text replacement (preserves formatting of unchanged text)
export interface TextReplacement {
  find: string; // Text to find
  replace: string; // Text to replace with
}

// The response from OpenAI should match this structure for modifications
export interface ShapeModification {
  /**
   * Shape ID - use the actual shape ID from presentation content for "modify"/"delete",
   * or any unique identifier like "new_1", "new_2" for "create" actions (ignored by system)
   */
  id: string;

  /** Action to perform on this shape */
  action: "create" | "modify" | "delete";

  /**
   * Optional layout-placeholder reference for shapes that FILL a placeholder on a
   * newly inserted slide (slide action="insert"). Set this to the placeholder's
   * `name` from deckLayoutCatalog.layouts[].placeholders[].name. The runtime
   * inserts the slide first, re-extracts its real placeholders, matches this name
   * to the generated shape id, then applies this shape as a "modify" on that real
   * placeholder (geometry is inherited from the placeholder, so left/top/width/height
   * can be omitted). Ignored on existing-slide edits, where the real shape `id`
   * should be used directly.
   */
  placeholderName?: string;

  // Content - for "create", include exactly ONE of: text, image payload, table, chart, or geometricType
  text?: string; // Full text replacement (loses formatting) - use textReplacements instead when possible
  textReplacements?: TextReplacement[]; // Find/replace (preserves formatting)
  paragraphOps?: ParagraphOp[]; // Paragraph-level edits (alignment, bullets, indent)

  // Position and size - REQUIRED for "create", optional for "modify"
  left?: number;
  top?: number;
  width?: number;
  height?: number;

  // For creating geometric shapes
  geometricType?: GeometricShapeType; // e.g. "rectangle", "ellipse", "star5"
  fillColor?: string; // Fill color (e.g. "blue", "#FF0000", "lightblue")
  lineColor?: string; // Outline color
  lineWidth?: number; // Outline width in points

  // Text styling (for text boxes and text inside geometric shapes)
  textColor?: string; // Text color (e.g. "white", "#000000")
  fontSize?: number; // Font size in points
  fontFamily?: string; // Font name (e.g. "Arial", "Calibri", "Georgia")
  bold?: boolean; // Make text bold
  italic?: boolean; // Make text italic
  verticalAlignment?: "top" | "middle" | "bottom";

  // For inserting images
  imageDataUri?: string; // Preferred inline payload: data:image/<mime>;base64,<bytes>
  imageBase64?: string; // Raw base64 payload without the data URI prefix
  imageMimeType?: string; // MIME type for imageBase64, e.g. "image/png"
  imageUrl?: string;
  /**
   * Brand icon catalog id (e.g. UBS `arrow-right`). Resolved to imageDataUri
   * by FactSet hydrate before host apply. LLM-visible only when the active brand
   * schema includes the field.
   */
  brandIconId?: string;
  /**
   * Requested company name from get_company_logo. Pair with logoCandidateRef;
   * runtime also uses this as text fallback when the selected ref cannot resolve.
   */
  logoCompanyName?: string;
  /** Opaque ref for the exact get_company_logo candidate selected by the model. */
  logoCandidateRef?: string;

  // For tables (single unified contract for create/edit/replace/relayout).
  table?: TableData;
  // Deprecated compatibility path. Prefer table.textUpdates via table.action="edit".
  cellUpdates?: CellUpdate[];

  /**
   * In-slide native PowerPoint chart payload for shape.action="create" or
   * full chart replacement/rebuild workflows. Schema matches FactSet's COM
   * Chart operation wire format (see ChartInfo).
   * In Office.js mode, charts are transparently expanded into a new chart
   * slide inserted after the source slide via chartTwoPassTranslator;
   * remaining shapes on the source slide are migrated onto the new chart slide.
   */
  chart?: ChartInfo;

  /**
   * Existing native PowerPoint chart patch for title/legend only.
   * Use with shape.action="modify" and the actual chart shape id.
   */
  chartEdit?: ChartEditInfo;

  // System-applied text-frame defaults for create operations, mirroring the
  // OfficeJS apply path. Not part of the LLM schema — set by the FactSet bridge
  // and ignored on modify so existing shapes aren't reflowed.
  wordWrap?: boolean;
  textVerticalAlignment?: "top" | "middle" | "bottom";
}

export type SnippetType =
  | "processFlow"
  | "funnel"
  | "pyramid"
  | "cycleDiagram"
  | "kpiDashboard"
  | "orgChart"
  | "comparisonColumns"
  | "quadrantGrid"
  | "iconStatRow"
  | "swimLanes"
  | "beforeAfter"
  | "chevronBanner"
  | "statGrid"
  | "sectionBlock"
  | "columnCards"
  | "iconTextRows"
  | "numberedRows"
  | "bulletBox"
  | "splitContent"
  | "tableCommentary"
  | "citiTextTable"
  | "citiQuadrant"
  | "citiSplitContent"
  | "citiTombstone"
  | "citiMatrix";

/** @deprecated Use SnippetType */
export type CompositeLayoutType = SnippetType;

export interface SnippetItem {
  label: string;
  description?: string;
  color?: string;
  bullets?: string[];
  value?: number;
  fontSize?: number;
  bold?: boolean;
  textColor?: string;
}

/** @deprecated Use SnippetItem */
export type CompositeLayoutItem = SnippetItem;

export interface Snippet {
  type: SnippetType;
  title?: string;
  items: SnippetItem[];
  axisLabels?: { x?: string; y?: string };
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  fontSize?: number;
  fontFamily?: string;
  textColor?: string;
  bold?: boolean;
  headerColor?: string;
}

/** @deprecated Use Snippet */
export type CompositeLayout = Snippet;

export interface SlideModification {
  /** Slide ID from presentation content (anchor for "insert"; target for other actions) */
  id: string;

  /** Slide index (0-based) - anchor for "insert"; target for other actions */
  index: number;

  /**
   * Optional user-facing summary for this slide operation. Used by opt-in
   * create-slide/deck-build progress streaming; ignored by the apply engine.
   */
  summary?: string;

  /** Displayed PowerPoint slide number for labels */
  slideNumber?: number;

  /**
   * Action to perform on this slide. Defaults to "modify" if not specified.
   * - "modify": Apply shape modifications to this existing slide
   * - "insert": Create a slide after this anchor, optionally using layoutName/slideMasterName, then apply shape modifications to the new slide
   * - "duplicate": Copy this slide, then apply shape modifications to the copy
   * - "delete": Remove this slide (shapes array is ignored)
   */
  action?: "modify" | "insert" | "duplicate" | "delete";

  /** Optional layout name to use for action="insert" when creating a slide from an existing deck layout. */
  layoutName?: string;

  /** Optional slide master name containing layoutName for action="insert". */
  slideMasterName?: string;

  /**
   * For action="duplicate": existing slide id to place the copy AFTER (the
   * insert anchor). `id` is the SOURCE slide being duplicated, so this is the
   * only way to control the copy's position. Defaults to the source slide
   * (copy lands right after the source) when omitted.
   */
  afterSlideId?: string;

  /** Shape modifications to apply to this slide */
  shapes: ShapeModification[];

  /**
   * Per-slide citation membership. Each id must refer to an entry in the
   * response's top-level `citations[]` array and represents a cited value
   * placed on this slide.
   */
  citationIds?: string[];

  /** Optional: Array of snippets — small composable visual elements positioned within the slide */
  snippets?: Snippet[];

  /** @deprecated Use snippets[] instead. Kept for backward compatibility. */
  compositeLayout?: CompositeLayout;
}

/**
 * Discriminator selecting the response shape returned by the model.
 *
 * - `"chat"`: conversational answer with no slide edits. The model
 *   populates `reply` (rendered as the assistant chat bubble) and
 *   omits `slides` / `summary`.
 * - `"edit"`: slide modifications. The model populates `slides` and
 *   optional `summary` (the Applied-Changes card header) and omits
 *   `reply`.
 *
 * Used by `useQueryHandler` to decide whether to invoke
 * `applyModifications` and by `ChatHistory` to choose between the
 * plain assistant bubble and the Applied-Changes card.
 */
export type ResponseMode = "chat" | "edit";

export interface PresentationModification {
  /**
   * Discriminator: `"chat"` (no slide edits, populate `reply`) or
   * `"edit"` (slide edits, populate `slides` and optional `summary`).
   * Always set after `finalizeModelModifications` runs (the finalizer
   * infers it from `slides.length` when the raw model output omits
   * it, attaching a `MODEL_OUTPUT_MISSING_MODE` diagnostic). Treated
   * as `"edit"` by downstream consumers when undefined so legacy
   * fixtures and older persisted messages keep working.
   */
  mode?: ResponseMode;
  /**
   * Chat-mode answer rendered as `ChatMessage.content` (the assistant
   * message bubble). Required when `mode === "chat"`; omitted when
   * `mode === "edit"`. Plain prose, 1–3 sentences in the user's voice.
   */
  reply?: string;
  slides: SlideModification[];
  /**
   * Edit-mode model-authored summary of the turn's intent (see
   * `OPENAI_MODIFICATION_JSON_SCHEMA.summary`). Used as the
   * `AppliedChangesRow` header; UI falls back to "Applied N changes to
   * Slide M" when missing. Only populated when `mode === "edit"`.
   */
  summary?: string;
  /** Optional: Internal diagnostics attached by provider/parser layers */
  diagnostics?: ApplyDiagnostic[];
  /**
   * Citations the assistant declares are relevant to the slide
   * modifications applied this turn. Emitted by the model as a separate
   * top-level section (see `OPENAI_MODIFICATION_JSON_SCHEMA.citations`)
   * so the runtime doesn't have to reverse-engineer cited UUIDs from
   * inline `<u id="…">VALUE</u>` markers in slide text. The model is
   * instructed to emit ONLY the citations whose underlying value was
   * actually placed on the slide — if a finster_query returned 5 cited
   * values but only 3 were used, only 3 entries should appear here.
   *
   * Each entry's `id`, `dataSource`, and `type` mirror the attributes
   * of the source `<u …>` tag from the finster_query result, and
   * `value` mirrors the displayed inner text. The runtime uses these
   * to populate the per-message Retrieved-sources / Citations cards.
   *
   * Optional and tolerated as missing on legacy conversations and
   * turns where finster_query was not used.
   */
  citations?: ModificationCitation[];
}

/**
 * One entry in `PresentationModification.citations`. See the schema
 * definition in `services/ai/openaiSchemas.ts` for the source-of-truth
 * description.
 */
export interface ModificationCitation {
  /** UUID of the cited value (matches the `<u id="…">` attribute). */
  id: string;
  /** `data-datasource` attribute from the source `<u …>` tag. */
  dataSource: string;
  /** `data-type` attribute from the source `<u …>` tag. */
  type: string;
  /** Displayed value placed on the slide (inner text of the `<u …>` tag). */
  value: string;
}

export type ApplyClassification =
  | "applied"
  | "partial_apply"
  | "true_no_op"
  | "target_mismatch"
  | "model_output_mismatch"
  | "apply_failed";

export type ApplyReasonCategory =
  | "no_op"
  | "target_mismatch"
  | "model_output_mismatch"
  | "apply_error";

export type ApplyReasonCode =
  | "NO_MODIFICATIONS_RETURNED"
  | "MODEL_OUTPUT_MISSING_SLIDES"
  | "MODEL_OUTPUT_MISSING_MODE"
  | "MODEL_OUTPUT_MISSING_REPLY"
  | "MODEL_OUTPUT_CHAT_WITH_SLIDES"
  | "MODEL_OUTPUT_CHAT_USED_SUMMARY"
  | "MODEL_OUTPUT_EDIT_NO_SLIDES"
  | "MODEL_OUTPUT_EDIT_WITH_REPLY"
  | "MODEL_OUTPUT_PARSE_FALLBACK_EMPTY"
  | "MODEL_OUTPUT_INVALID_PARAGRAPH_OP"
  | "MODEL_OUTPUT_INVALID_TABLE_OP"
  | "MODEL_OUTPUT_SLIDE_LEVEL_CITATIONS"
  | "MODEL_OUTPUT_INVALID_NATIVE_CHART"
  | "MODEL_OUTPUT_CHART_OPTION_DROPPED"
  | "MODEL_OUTPUT_CHART_OPTION_NORMALIZED"
  | "MODEL_OUTPUT_CHART_TYPE_REMAPPED"
  | "MODEL_OUTPUT_CHART_RISK_REMAPPED"
  | "MODEL_OUTPUT_CHART_RISK_REJECTED"
  | "MODEL_OUTPUT_CHART_LAYOUT_CLAMPED"
  | "MODEL_OUTPUT_CHART_SERIES_NORMALIZED"
  | "MODEL_OUTPUT_CHART_PERCENT_COERCED"
  | "SLIDE_INDEX_OUT_OF_RANGE"
  | "SHAPE_ID_NOT_FOUND"
  | "PARAGRAPH_TARGET_INDEX_OUT_OF_RANGE"
  | "NO_MUTATION_FIELDS"
  | "IMAGE_PREFETCH_FAILED"
  | "IMAGE_INSERT_UNSUPPORTED"
  | "TABLE_INSERT_FAILED"
  | "TABLE_TARGET_NOT_TABLE"
  | "TABLE_OPERATION_FAILED"
  | "TABLE_EDIT_FAILED"
  | "TABLE_REPLACE_FAILED"
  | "TABLE_STRUCTURE_OP_FAILED"
  | "TABLE_STYLE_OP_FAILED"
  | "TABLE_CLEAR_FAILED"
  | "TABLE_RELAYOUT_FAILED"
  | "TABLE_SIGNATURE_VALIDATION_FAILED"
  | "CHART_INSERT_FAILED"
  | "SHAPE_CREATE_FAILED"
  | "SHAPE_MODIFY_FAILED"
  | "PARAGRAPH_OP_APPLY_FAILED"
  | "SHAPE_DELETE_FAILED"
  | "SLIDE_INSERT_UNSUPPORTED"
  | "SLIDE_INSERT_FAILED"
  | "SLIDE_DUPLICATE_FAILED"
  | "PLACEHOLDER_FILL_UNRESOLVED";

export interface ApplyDiagnostic {
  code: ApplyReasonCode;
  category: ApplyReasonCategory;
  message: string;
  slideIndex?: number;
  slideId?: string;
  shapeId?: string;
  action?: "create" | "modify" | "delete" | "insert" | "duplicate";
}

export interface ApplyActionCounts {
  create: number;
  modify: number;
  delete: number;
  duplicate: number;
}

export interface ApplyOperationCounts {
  slides: number;
  shapes: number;
  byAction: ApplyActionCounts;
}

/**
 * One slide's citation provenance, keyed by the REAL host slide id the apply
 * pipeline assigned (so newly inserted/duplicated slides bind correctly without
 * any post-build createdSlides diff or positional pairing).
 */
export interface SlideCitationBinding {
  /** Real host slide id (post-materialization). */
  slideId: string;
  /** Slide index at apply time (consumers may re-key to the final index). */
  slideIndex: number;
  slideNumber?: number;
  /** Slide-level citationIds[] the model declared for this slide. */
  declaredCitationIds: string[];
  /** Inline `<u id>` markup refs, per shape id (from stripModificationsInPlace). */
  shapeRefs: Record<string, string[]>;
}

/**
 * Authoritative per-slide citation map emitted by `applyModifications`, built
 * from the post-materialization modifications. The single source of truth for
 * attaching SlideCitationsView messages by real slide id.
 */
export interface CitationManifest {
  bindings: SlideCitationBinding[];
  allCitationIds: string[];
}

export interface ApplyResultSummary {
  requested: ApplyOperationCounts;
  applied: ApplyOperationCounts;
  skipped: {
    count: number;
    byReason: Partial<Record<ApplyReasonCode, number>>;
    issues: ApplyDiagnostic[];
  };
  errors: {
    count: number;
    byReason: Partial<Record<ApplyReasonCode, number>>;
    issues: ApplyDiagnostic[];
  };
  classification: ApplyClassification;
  /**
   * Per-slide citation bindings keyed by real slide id. Built from the
   * materialized modifications, so insert/duplicate ops (which carry no real id
   * in the model payload) are already resolved. Undefined when no citations.
   */
  citationManifest?: CitationManifest;
}

export interface ChatTaskContext {
  content: string;
  name: string;
  citations?: import("./citations").Citation[];
}

// Tool call types
export type ActiveGraphOperation =
  | "create_active_graph"
  | "inspect_selected_active_graph"
  | "list_active_graph_stylesets"
  | "update_active_graph_frame"
  | "delete_selected_active_graph";

export interface ActiveGraphTarget {
  slideId?: string;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
}

export interface ActiveGraphToolInput {
  operation: ActiveGraphOperation;
  chartParameters?: FactsetChartParameters;
  titleOverride?: string;
  target?: ActiveGraphTarget;
}

export interface ToolCall {
  tool:
    | "get_company_logo"
    | "finster_query"
    | "factset_ai_query"
    | "get_slide_images"
    | "apply_edits"
    | "get_slides_info"
    | "insert_library_slide"
    | "apply_preslink"
    | "list_slide_library_collections"
    | "search_slide_library"
    | "get_library_slide_previews"
    | "present_deck_plan"
    | "update_deck_plan"
    | "active_graph"
    | "list_active_graph_types"
    | "id_lookup";
  company_name?: string;
  company_names?: string[];
  size?: number;
  query?: string;
  queryMode?: FinsterQueryMode; // finster_query: "standard" (fast) vs "thinking" (deep multi-hop)
  activeGraphInput?: ActiveGraphToolInput;
  idLookupSearchText?: string;
  slide_numbers?: number[]; // Displayed slide numbers from deckContext.includedSlides[*].slideNumber
  max_images?: number; // Optional cap for returned reference images
  // insert_library_slide: insert a pre-built slide-library slide at a position.
  librarySlideId?: string; // librarySlideId from the approved plan's per-slide directive
  afterSlideId?: string; // real slide id to insert AFTER (the previously created slide)
  // Agentic edit/observe primitives:
  modifications?: PresentationModification; // apply_edits batch payload
  returnJson?: boolean; // apply_edits return.json / get_slides_info json
  returnImage?: boolean; // apply_edits return.image / get_slides_info image
  returnLayouts?: boolean; // get_slides_info layouts
  layout_names?: string[]; // get_slides_info layout_names
  returnDesigns?: boolean; // get_slides_info designs
  returnMasterShapes?: boolean; // get_slides_info master_shapes
  // apply_preslink: shapes whose PresLink-linked source files to resolve.
  preslinkOperation?: "refresh" | "inspect"; // apply_preslink: agent-selected intent for this call
  preslinkShapeRefs?: Array<{ slideId: string; id: string; name?: string }>;
  includePptFile?: boolean; // apply_preslink: also return the presentation archive
  includeCellData?: boolean; // apply_preslink: parse linked workbooks into a text preview
  processPresentationFile?: boolean; // apply_preslink: refresh linked files and apply processed deck
  preslinkScope?: "shapes" | "deck"; // apply_preslink: target supplied shapes or discover the full deck
  // search_slide_library
  collectionId?: string;
  collectionIds?: string[];
  slideLibraryScope?: "all";
  tags?: string[];
  offset?: number;
  limit?: number;
  // get_library_slide_previews
  slideIds?: string[];
  // present_deck_plan / update_deck_plan
  deckPlan?: import("./services/ai/deckPlannerService").DeckPlan;
  deckPlanPatches?: Array<
    Partial<import("./services/ai/deckPlannerService").SlideOutline> & { index: number }
  >;
}

export interface ToolResult {
  tool: string;
  success: boolean;
  data?: unknown;
  error?: string;
}

export type {
  CreateSkillTurnMetadataInput,
  SkillReferenceSnapshot,
  SkillSelectionPath,
  SkillTurnMetadata,
} from "../shared/skills/turnMetadata";
export {
  createPendingSkillTurnMetadata,
  createSkillTurnMetadata,
  normalizeSkillReferenceSnapshots,
  readSkillTurnMetadata,
} from "../shared/skills/turnMetadata";

// Chat message in a session
export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  modifications?: PresentationModification; // What was applied (for assistant messages)
  slideIndex?: number; // Which slide was targeted
  slideNumber?: number; // Displayed PowerPoint slide number for labels
  thinking?: string; // AI reasoning (for assistant messages)
  /** Persisted agent hop timeline (capped) for replay of the streamed run. */
  agentTimeline?: AgentTimelineState;
  /**
   * Display-safe per-turn Skill snapshot. Raw guidance, descriptions, target
   * metadata, file names, and evidence must never be persisted here.
   */
  skillMetadata?: SkillTurnMetadata;
  /**
   * Structured citations surfaced during this assistant turn (typically from
   * one or more `finster_query` tool calls). Persisted with the message so a
   * later click on a `<u id="…">` in the rendered content can resolve to the
   * full citation record.
   */
  citations?: import("./citations").Citation[];
  /** Map from inline citation UUID -> index into `citations`. */
  citationsById?: Record<string, number>;
  /**
   * Map from cited UUID -> the displayed `VALUE` the model placed on
   * the slide. Populated from `modifications.citations[]` (the model's
   * declared citation section). Used by the Retrieved-sources cards
   * to surface the exact quoted value next to each source document,
   * e.g. "$281.7 billion" next to
   * "Microsoft Corporation FY 2025 Form 10-K".
   *
   * IMPORTANT: `citations`/`citationsById`/`citedValuesByUuid` are
   * PER-SLIDE provenance. `SlideCitationsView` (the "Citations" tab)
   * aggregates them across the slide tab's messages, so they must only
   * carry what actually landed on THIS slide. Turn-level retrieval that
   * landed elsewhere belongs on `retrievedCitations` below.
   */
  citedValuesByUuid?: Record<string, string>;
  /**
   * Turn-level retrieved sources surfaced during this assistant turn
   * (e.g. from `finster_query` / `factset_ai_query`). Rendered by the
   * "Retrieved sources" accordion on the chat bubble, on the slide where
   * the chat happened.
   *
   * Unlike `citations`/`citedValuesByUuid` (per-slide, drive the
   * Citations tab), these are turn-scoped and are intentionally IGNORED
   * by `SlideCitationsView`, so that a chat on the source slide does not
   * pollute the source slide's Citations tab with values that were
   * actually placed on other slides edited during the turn.
   */
  retrievedCitations?: import("./citations").Citation[];
  /** Turn-level cited UUID -> displayed value, paired with `retrievedCitations`. */
  retrievedCitedValuesByUuid?: Record<string, string>;
}

// A chat session (one per tab)
export interface ChatSession {
  id: string;
  name: string; // "Slide 3" or custom name
  messages: ChatMessage[];
  slideId: string; // Stable slide identity
  slideIndex: number; // Cached index for display/navigation fallback
  slideNumber?: number; // Displayed PowerPoint slide number for UI labels
  /**
   * Your Files the user attached to this chat, including the files a Skill
   * brought to a request this chat accepted. Owned by the session rather than
   * the composer so a file cannot follow the user into another slide, deck or
   * account — the same scoping Excel gives `ExcelChatSession`.
   */
  selectedUserFileIds?: string[];
  createdAt: number;
  updatedAt: number;
}

// A single tab
export interface Tab {
  id: string;
  session: ChatSession;
  isLoading: boolean;
  loadingStatus: string;
  streamingThinking: string;
  /**
   * Live agent hop timeline for the in-flight turn (streamed thinking, tool
   * calls/results, nested finster sub-agent). Additive: the existing
   * `streamingThinking` pane is unchanged and used as a fallback.
   */
  agentTimeline?: AgentTimelineState;
  /** Monotonic signal used to collapse the live Thought process pane for this turn. */
  thoughtProcessCollapseSignal?: number;
  visionFeedback: string;
  refinementStep: number;
}

// All tabs state
export interface TabsState {
  /**
   * Presentation this state belongs to. Embedded so save/load and async
   * cross-file updates can route by the state's own owner instead of a
   * mutable ref the FactSet poll can flip mid-commit. Always set on
   * production paths; optional only for legacy test fixtures.
   */
  documentId?: string;
  tabs: Tab[];
  activeTabId: string;
}

// Helper to generate unique IDs
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
